import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Check, Star, X } from 'lucide-react';
import { tenantSelector } from '../../../selector/authenticationSelector';
import CustomButton from '../../../components/CustomButton';
import RowActionsMenu from '../../../components/RowActionsMenu';
import { cn } from '../../../utils/utils';
import NativeSelect from '../../../components/ui/NativeSelect';
import { tableCellClass, tableHeaderCellClass } from '../../../components/TableCard';
import OrgDetailSection from './OrgDetailSection';
import {
    BulkResult,
    MemberRow,
    OrganizationUnit,
} from '../../../api/organization/organizationApiTypes';
import {
    useBulkAddOrganizationMembersMutation,
    useGetOrganizationMembersQuery,
    useRemoveOrganizationMemberMutation,
    useSetMembershipUnitMutation,
    useSetOrganizationPrimaryContactMutation,
} from '../../../api/organization/organizationApiSlice';
import { parseMemberEmails } from '../../../utils/memberCsv';
import { STATION_TIME_ZONE } from '../../../utils/stationTime';

interface OrgMembersSectionProps {
    organizationId: string;
    /** The org's units, for resolving a member's unit name. */
    units: OrganizationUnit[];
}

/**
 * The unified member list of an organization (plan §4.3a, #1721 step 5):
 * open e-mail invites and account-backed memberships rendered as ONE list —
 * the storage split is invisible to the admin. Adding members takes a pasted
 * e-mail list or a CSV upload, parsed/validated/deduped client-side
 * (memberCsv.ts), submitted in one bulk call, with the backend's per-e-mail
 * outcomes rendered afterwards. Removal offboards both states in one call.
 *
 * Deliberately NOT part of the page's view/edit form state: members are
 * backed by dedicated endpoints and mutate immediately, independent of the
 * org form's Save/Cancel cycle.
 *
 * Adding a member takes an e-mail address and nothing else. The role picker
 * that briefly stood here is gone (PO, 2026-08-21): `organization_membership`
 * still carries a `role` column defaulting to `member`, and the write
 * endpoints still accept one, but no admin surface offers it — so every member
 * added here is a `member`. Role assignment, if it comes back, belongs to the
 * H-3c membership surface together with the permissions that would make a role
 * mean something.
 */
const OrgMembersSection: React.FC<OrgMembersSectionProps> = ({ organizationId, units }) => {
    const { t, i18n } = useTranslation();

    // skip until the tenant is restored — hard-reload race with the tenant
    // cache reset middleware (see OrganizationsTab).
    const tenant = useSelector(tenantSelector);
    const { data: members, isLoading } = useGetOrganizationMembersQuery(
        { organizationId },
        { skip: !tenant },
    );
    const [bulkAddMembers, { isLoading: isAdding }] = useBulkAddOrganizationMembersMutation();
    const [removeMember, { isLoading: isRemoving }] = useRemoveOrganizationMemberMutation();
    const [setPrimaryContact, { isLoading: isSettingContact }] =
        useSetOrganizationPrimaryContactMutation();
    const [setMembershipUnit, { isLoading: isSettingUnit }] = useSetMembershipUnitMutation();

    const [emailInput, setEmailInput] = React.useState('');
    const [inputError, setInputError] = React.useState<string | null>(null);
    const [outcomes, setOutcomes] = React.useState<BulkResult[] | null>(null);
    const [pendingRemoveEmail, setPendingRemoveEmail] = React.useState<string | null>(null);
    // The one member whose unit is being changed, and the unit picked so far.
    // Nothing is saved until the check mark (design-decisions › Bearbeiten und
    // Speichern): picking a unit only changes this draft.
    const [unitEdit, setUnitEdit] = React.useState<{ membershipId: string, draft: string } | null>(null);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    // "Einheit ändern" comes from the row's menu, which hands focus back to its
    // trigger as it closes. Focus moves to the select once that has happened,
    // so the keyboard lands where the edit is.
    const editingMembershipId = unitEdit?.membershipId;
    React.useEffect(() => {
        if (!editingMembershipId) return undefined;
        const timer = window.setTimeout(() => {
            document.getElementById(`member-unit-${editingMembershipId}`)?.focus();
        }, 0);
        return () => window.clearTimeout(timer);
    }, [editingMembershipId]);

    const unitName = (unitId?: string) => {
        if (!unitId) return t('common.not_applicable');
        return units.find((unit) => unit.id === unitId)?.name ?? t('common.not_applicable');
    };

    const submitEmails = async (raw: string) => {
        setInputError(null);
        setOutcomes(null);
        const parsed = parseMemberEmails(raw);
        if (parsed.valid.length === 0) {
            setInputError(t('organization.members.no_valid_emails'));
            return;
        }
        try {
            const results = await bulkAddMembers({
                organizationId,
                // No role on the wire: the endpoint defaults to `member`,
                // which is also the column default, so the two agree.
                body: { emails: parsed.valid },
            }).unwrap();
            // Client-side rejects (malformed tokens) join the backend's
            // per-e-mail outcomes so the admin sees one complete result list.
            setOutcomes([
                ...results,
                ...parsed.invalid.map((email): BulkResult => ({ email, outcome: 'invalid_email' })),
            ]);
            setEmailInput('');
        } catch (err) {
            const e = err as { data?: { message?: string } };
            setInputError(e?.data?.message ?? t('organization.members.add_error'));
        }
    };

    const handleCsvFile = async (files: FileList | null) => {
        const file = files?.[0];
        if (!file) return;
        const text = await file.text();
        await submitEmails(text);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleConfirmRemove = async () => {
        if (!pendingRemoveEmail) return;
        setInputError(null);
        try {
            await removeMember({ organizationId, email: pendingRemoveEmail }).unwrap();
            setPendingRemoveEmail(null);
        } catch (err) {
            const e = err as { data?: { message?: string } };
            setInputError(e?.data?.message ?? t('organization.members.remove_error'));
            setPendingRemoveEmail(null);
        }
    };

    /**
     * Designating a contact is the switch that gives that person member
     * administration — and, since #3037, management of the organization's
     * bookings — in the end-user app; there is no separate feature flag.
     * Several contacts are allowed (#3037): the star toggles this one member
     * and never touches another.
     */
    const handleToggleContact = async (member: MemberRow) => {
        if (!member.membershipId) return;
        setInputError(null);
        try {
            await setPrimaryContact({
                organizationId,
                membershipId: member.membershipId,
                isPrimaryContact: !member.isPrimaryContact,
            }).unwrap();
        } catch (err) {
            const e = err as { data?: { message?: string } };
            setInputError(e?.data?.message ?? t('organization.members.contact_error'));
        }
    };

    /**
     * Which unit a member books for (#1810). It decides what their covered
     * bookings are attributed to, the cost centre those start with, and the
     * Leitweg-ID their invoices are routed to. Changed per member: "Einheit ändern" in the
     * row's menu opens the select, the check mark saves, the cross discards — never on
     * the pick itself.
     */
    const handleUnitSave = async (member: MemberRow) => {
        if (!member.membershipId || !unitEdit) return;
        setInputError(null);
        try {
            await setMembershipUnit({
                organizationId,
                membershipId: member.membershipId,
                unitId: unitEdit.draft === '' ? null : unitEdit.draft,
            }).unwrap();
            setUnitEdit(null);
        } catch {
            // The server's refusals here are English sentences for the API
            // reader (a unit of another organisation, an archived one) — the
            // select offers neither, so the operator's own text is enough.
            setInputError(t('organization.members.unit_error'));
        }
    };

    /** An active member of an organisation that has units can change unit —
     *  an invite has no membership to change, and without units there is
     *  nothing to choose. */
    const canEditUnit = (member: MemberRow) =>
        member.state === 'active' && !!member.membershipId && units.length > 0;

    /**
     * The unit cell: the unit's name. "Einheit ändern" in the row's menu turns
     * it into a select with a check mark (save) and a cross (discard).
     */
    const unitCell = (member: MemberRow) => {
        const membershipId = member.membershipId;
        if (!canEditUnit(member) || !membershipId) {
            return unitName(member.unitId);
        }
        const stored = member.unitId ?? '';
        // A unit archived since it was assigned is no longer offered; it stays
        // shown as the current value until someone picks another.
        const archived = member.unitId && !units.some((unit) => unit.id === member.unitId);
        if (unitEdit?.membershipId !== membershipId) {
            return archived ? t('organization.members.unit_archived') : unitName(member.unitId);
        }
        return (
            <span className={'inline-flex items-center gap-1'}>
                <NativeSelect
                    id={`member-unit-${membershipId}`}
                    aria-label={t('organization.members.unit_select', { email: member.email })}
                    value={unitEdit.draft}
                    disabled={isSettingUnit}
                    onChange={(event) => setUnitEdit({ membershipId, draft: event.target.value })}
                >
                    <option value={''}>{t('organization.members.no_unit')}</option>
                    {archived && (
                        <option value={member.unitId} disabled>{t('organization.members.unit_archived')}</option>
                    )}
                    {units.map((unit) => (
                        <option key={unit.id} value={unit.id}>{unit.name}</option>
                    ))}
                </NativeSelect>
                <button
                    type={'button'}
                    onClick={() => { void handleUnitSave(member); }}
                    disabled={isSettingUnit || unitEdit.draft === stored}
                    title={t('common.save')}
                    aria-label={t('common.save')}
                    className={'p-1 text-foreground/60 hover:text-foreground transition-colors disabled:opacity-40'}
                >
                    <Check className={'h-4 w-4'} />
                </button>
                <button
                    type={'button'}
                    onClick={() => { setInputError(null); setUnitEdit(null); }}
                    disabled={isSettingUnit}
                    title={t('common.cancel')}
                    aria-label={t('common.cancel')}
                    className={'p-1 text-foreground/60 hover:text-foreground transition-colors disabled:opacity-40'}
                >
                    <X className={'h-4 w-4'} />
                </button>
            </span>
        );
    };

    /** One label for the star's title and aria-label, so the two cannot drift. */
    const contactActionLabel = (member: MemberRow) =>
        member.isPrimaryContact
            ? t('organization.members.contact_clear')
            : t('organization.members.contact_set');

    const formatJoinedAt = (joinedAt?: string) => {
        if (!joinedAt) return t('common.not_applicable');
        return new Date(joinedAt).toLocaleDateString(i18n.language, { timeZone: STATION_TIME_ZONE });
    };

    return (
        <OrgDetailSection
            title={t('organization.sections.members')}
            subtitle={t('organization.sections.members_subtitle')}
            help={t('organization.help.members')}
        >
            <div className={'flex flex-col gap-4'}>
                {inputError && (
                    <div className={'bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-800'}>
                        {inputError}
                    </div>
                )}

                {isLoading ? (
                    <p className={'text-sm text-foreground/60'}>{t('common.loading')}</p>
                ) : !members || members.length === 0 ? (
                    <p className={'text-sm text-foreground/60'}>
                        {t('organization.members.empty')}
                    </p>
                ) : (
                    <div className={'overflow-x-auto'}>
                        <table className={'w-full text-sm'}>
                            <thead>
                                <tr className={'border-b border-foreground/5'}>
                                    <th className={tableHeaderCellClass}>
                                        {t('organization.members.columns.email')}
                                    </th>
                                    <th className={tableHeaderCellClass}>
                                        {t('organization.members.columns.status')}
                                    </th>
                                    <th className={tableHeaderCellClass}>
                                        {t('organization.members.columns.unit')}
                                    </th>
                                    <th className={tableHeaderCellClass}>
                                        {t('organization.members.columns.joined')}
                                    </th>
                                    <th className={tableHeaderCellClass} />
                                </tr>
                            </thead>
                            <tbody>
                                {members.map((member: MemberRow) => (
                                    <tr
                                        key={`${member.state}-${member.email}`}
                                        className={'border-b border-foreground/5 last:border-b-0'}
                                    >
                                        <td className={tableCellClass}>
                                            {member.state === 'anonymized'
                                                ? t('common.not_applicable')
                                                : member.email}
                                        </td>
                                        <td className={tableCellClass}>
                                            {t(`organization.members.state.${member.state}`)}
                                        </td>
                                        <td className={tableCellClass}>{unitCell(member)}</td>
                                        <td className={tableCellClass}>
                                            {formatJoinedAt(member.joinedAt)}
                                        </td>
                                        <td className={`${tableCellClass} text-right`}>
                                            <span className={'inline-flex items-center gap-1'}>
                                                {/* The contact mark is a status, shown on the
                                                    row; setting or clearing it changes something,
                                                    so that sits in the row's menu (design-decisions.md
                                                    › Zeilenaktionen in Tabellen). A pending owner
                                                    invite is named as a contact before they ever
                                                    signed in — shown, not togglable: there is no
                                                    membership yet. */}
                                                {member.isPrimaryContact && member.state !== 'anonymized' && (
                                                    <span
                                                        title={member.state === 'invited'
                                                            ? t('organization.members.contact_pending')
                                                            : t('organization.members.contact_current')}
                                                        aria-label={member.state === 'invited'
                                                            ? t('organization.members.contact_pending')
                                                            : t('organization.members.contact_current')}
                                                        className={cn(
                                                            'inline-flex p-2',
                                                            member.state === 'invited' ? 'text-foreground/40' : 'text-foreground/60',
                                                        )}
                                                    >
                                                        <Star className={'h-4 w-4'} fill={'currentColor'} />
                                                    </span>
                                                )}
                                                <RowActionsMenu
                                                    rowName={member.email}
                                                    items={[
                                                        {
                                                            id: 'unit',
                                                            label: t('organization.members.unit_change'),
                                                            onClick: () => {
                                                                setInputError(null);
                                                                setUnitEdit({
                                                                    membershipId: member.membershipId ?? '',
                                                                    draft: member.unitId ?? '',
                                                                });
                                                            },
                                                            hidden: !canEditUnit(member),
                                                        },
                                                        {
                                                            // Only an active membership can hold the
                                                            // mark — an unclaimed invite has none.
                                                            id: 'contact',
                                                            label: contactActionLabel(member),
                                                            onClick: () => { void handleToggleContact(member); },
                                                            disabled: isSettingContact,
                                                            hidden: member.state !== 'active' || !member.membershipId,
                                                        },
                                                    ]}
                                                    destructiveItems={[{
                                                        id: 'remove',
                                                        label: t('organization.members.remove'),
                                                        onClick: () => setPendingRemoveEmail(member.email),
                                                        hidden: member.state === 'anonymized',
                                                    }]}
                                                />
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {pendingRemoveEmail && (
                    <div className={'flex items-center gap-3 bg-background rounded-lg px-4 py-3'}>
                        <p className={'text-sm text-foreground/80 flex-1'}>
                            {t('organization.members.remove_confirm', { email: pendingRemoveEmail })}
                        </p>
                        <CustomButton
                            title={t('common.cancel')}
                            onClick={() => setPendingRemoveEmail(null)}
                            isFilled={false}
                        />
                        <CustomButton
                            title={t('organization.members.remove')}
                            onClick={handleConfirmRemove}
                            loading={isRemoving}
                            variant={'delete'}
                        />
                    </div>
                )}

                <div className={'flex flex-col gap-2'}>
                    <label className={'text-sm font-medium text-foreground/80'}>
                        {t('organization.members.add_label')}
                    </label>
                    <textarea
                        value={emailInput}
                        onChange={(event) => setEmailInput(event.target.value)}
                        placeholder={t('organization.members.add_placeholder')}
                        rows={3}
                        className={'w-full rounded-md border border-foreground/15 px-3 py-2 text-sm '
                            + 'bg-text-contrast text-foreground focus:outline-none focus:border-foreground/40'}
                    />
                    <div className={'flex gap-3'}>
                        <CustomButton
                            title={t('organization.members.add_button')}
                            onClick={() => submitEmails(emailInput)}
                            loading={isAdding}
                            disabled={emailInput.trim().length === 0}
                        />
                        <CustomButton
                            title={t('organization.members.upload_csv')}
                            onClick={() => fileInputRef.current?.click()}
                            isFilled={false}
                            disabled={isAdding}
                        />
                        <input
                            ref={fileInputRef}
                            type={'file'}
                            accept={'.csv,.txt,text/csv,text/plain'}
                            className={'hidden'}
                            onChange={(event) => handleCsvFile(event.target.files)}
                        />
                    </div>
                </div>

                {outcomes && outcomes.length > 0 && (
                    <div className={'flex flex-col gap-1'}>
                        <p className={'text-sm font-medium text-foreground/80'}>
                            {t('organization.members.outcomes_title')}
                        </p>
                        <ul className={'text-sm text-foreground/70'}>
                            {outcomes.map((result) => (
                                <li key={result.email} className={'py-0.5'}>
                                    <span className={'flex justify-between gap-4'}>
                                        <span>{result.email}</span>
                                        <span className={result.outcome === 'invalid_email' ? 'text-foreground' : 'text-foreground/60'}>
                                            {t(`organization.members.outcome.${result.outcome}`)}
                                        </span>
                                    </span>
                                    {/* Belonging to two organizations is allowed, but it decides
                                        who a covered booking is billed to (#1926) — the admin
                                        creating it should see that they are. */}
                                    {result.otherOrganizations && result.otherOrganizations.length > 0 && (
                                        <span className={'block text-foreground/60'}>
                                            {t('organization.members.also_member_of', {
                                                organizations: result.otherOrganizations.join(', '),
                                            })}
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </OrgDetailSection>
    );
};

export default OrgMembersSection;
